import { Controller } from 'stimulus';

import { createVoiceDisguise } from './voice_disguise';

const MAX_DURATION_MS = 5 * 60 * 1000; // 5 minutes
const PREFERRED_MIME_TYPES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'];

const AudioContextClass =
  typeof window !== 'undefined' ? window.AudioContext || window.webkitAudioContext : undefined;

export default class AudioRecorderController extends Controller {
  static targets = ['fileInput', 'idleState', 'recordingState', 'recordedState', 'timer', 'playback'];

  connect() {
    // MediaRecorder and Web Audio are unavailable in some browsers/environments;
    // guard so the controller (and the rest of the page's JS) doesn't blow up on
    // connect. Web Audio is required, not optional: anonymous recordings must be
    // voice-disguised, so without it we must not record at all rather than risk
    // leaking the whistleblower's real voice.
    this.supported = typeof MediaRecorder !== 'undefined' && typeof AudioContextClass !== 'undefined';
    this.mimeType = this.supported
      ? PREFERRED_MIME_TYPES.find((t) => MediaRecorder.isTypeSupported(t)) || ''
      : '';
    this.chunks = [];
    this.timerInterval = null;
    this.elapsedSeconds = 0;
    this.recorder = null;
    this.stream = null;
    this.audioContext = null;
    this.disguise = null;
    // The hint label doubles as the live timer while recording, so remember its
    // default copy to restore once recording stops.
    this.defaultHint = this.timerTarget.textContent;

    // A finished recording bakes in (or omits) the disguise at capture time and
    // the raw voice is never kept, so it cannot be re-matched if the reporter
    // later flips their anonymity choice. Watch that checkbox and discard any
    // existing recording on change, forcing a re-record under the new setting.
    // This also prevents a real voice from ending up on a now-anonymous report.
    this.anonymityCheckbox =
      this.element.closest('form')?.querySelector('input[name="personal_details"]') ??
      document.querySelector('input[name="personal_details"]');
    if (this.anonymityCheckbox) {
      this._onAnonymityChange = this._onAnonymityChange.bind(this);
      this.anonymityCheckbox.addEventListener('change', this._onAnonymityChange);
    }
  }

  disconnect() {
    clearTimeout(this.capTimeout);
    this._clearTimer();
    this._stopStream();
    if (this.anonymityCheckbox && this._onAnonymityChange) {
      this.anonymityCheckbox.removeEventListener('change', this._onAnonymityChange);
    }
  }

  _onAnonymityChange() {
    const hasRecording =
      this.fileInputTarget.files.length > 0 ||
      !this.recordedStateTarget.classList.contains('hidden');
    if (hasRecording) this.discard();
  }

  async start() {
    if (!this.supported) return;

    try {
      this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      return;
    }

    this.chunks = [];
    this.elapsedSeconds = 0;

    // Only anonymous complaints are disguised (see _shouldDisguise). When
    // anonymous, route the microphone through the voice disguiser and record
    // its output, never the raw mic stream — so the original voice never leaves
    // the device. When the reporter has identified themselves, record normally.
    const recordStream = this._shouldDisguise()
      ? this._buildDisguisedStream(this.stream)
      : this.stream;

    this.recorder = new MediaRecorder(
      recordStream,
      this.mimeType ? { mimeType: this.mimeType } : {}
    );

    this.recorder.addEventListener('dataavailable', (e) => {
      if (e.data.size > 0) this.chunks.push(e.data);
    });

    this.recorder.addEventListener('stop', () => this._onRecordingStop());

    this.recorder.start(100);
    this._showState('recording');
    this._startTimer();

    this.capTimeout = setTimeout(() => this.stop(), MAX_DURATION_MS);
  }

  stop() {
    if (this.recorder && this.recorder.state !== 'inactive') {
      clearTimeout(this.capTimeout);
      this.recorder.stop();
    }
  }

  discard() {
    this._reset();
    this._showState('idle');
  }

  async recordAgain() {
    // Reset without flashing the idle state — start() takes over the UI once the
    // mic stream resolves.
    this._reset();
    await this.start();
  }

  _reset() {
    this.fileInputTarget.value = '';
    this._stopStream();
    this._clearTimer();
    if (this.playbackTarget.src) {
      URL.revokeObjectURL(this.playbackTarget.src);
      this.playbackTarget.src = '';
    }
  }

  _onRecordingStop() {
    this._clearTimer();
    this._stopStream();

    const blob = new Blob(this.chunks, { type: this.mimeType || 'audio/webm' });
    this.playbackTarget.src = URL.createObjectURL(blob);

    // Hand the recording to a regular multipart file input. The blob is uploaded
    // and verified server-side on form submit, exactly like other attachments —
    // no client-side checksum or direct upload involved.
    const extension = (this.mimeType || 'audio/webm').split('/')[1]?.split(';')[0] || 'webm';
    const file = new File([blob], `recording.${extension}`, { type: blob.type || 'audio/webm' });
    const dataTransfer = new DataTransfer();
    dataTransfer.items.add(file);
    this.fileInputTarget.files = dataTransfer.files;

    this._showState('recorded');
  }

  _startTimer() {
    this.elapsedSeconds = 0;
    this._updateTimerDisplay();
    this.timerInterval = setInterval(() => {
      this.elapsedSeconds += 1;
      this._updateTimerDisplay();
    }, 1000);
  }

  _clearTimer() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
    this.timerTarget.textContent = this.defaultHint;
  }

  _updateTimerDisplay() {
    const m = String(Math.floor(this.elapsedSeconds / 60)).padStart(2, '0');
    const s = String(this.elapsedSeconds % 60).padStart(2, '0');
    this.timerTarget.textContent = `${m}:${s}`;
  }

  _shouldDisguise() {
    // Creation form: anonymity is chosen live — the "provide personal details"
    // checkbox unchecked means the report is anonymous. Reply form: there is no
    // checkbox, so the server marks the complaint's anonymity via
    // data-audio-recorder-disguise. If neither is present, default to disguising
    // so we never accidentally expose an anonymous reporter's voice.
    const checkbox =
      this.element.closest('form')?.querySelector('input[name="personal_details"]') ??
      document.querySelector('input[name="personal_details"]');
    if (checkbox) return !checkbox.checked;

    return this.data.get('disguise') !== 'false';
  }

  _buildDisguisedStream(stream) {
    this.audioContext = new AudioContextClass();
    // A freshly created context can start suspended under autoplay policies;
    // start() runs from a click, so resuming here is allowed.
    if (this.audioContext.state === 'suspended') this.audioContext.resume();

    const source = this.audioContext.createMediaStreamSource(stream);
    const destination = this.audioContext.createMediaStreamDestination();

    this.disguise = createVoiceDisguise(this.audioContext);
    source.connect(this.disguise.input);
    this.disguise.output.connect(destination);
    this.disguise.start();

    return destination.stream;
  }

  _stopStream() {
    if (this.disguise) {
      this.disguise.stop();
      this.disguise = null;
    }
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
    if (this.stream) {
      this.stream.getTracks().forEach((t) => t.stop());
      this.stream = null;
    }
  }

  _showState(state) {
    this.idleStateTarget.classList.toggle('hidden', state !== 'idle');
    this.recordingStateTarget.classList.toggle('hidden', state !== 'recording');
    this.recordedStateTarget.classList.toggle('hidden', state !== 'recorded');
    this.playbackTarget.classList.toggle('hidden', state !== 'recorded');
  }
}
